import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FadeInUp } from './MotionWrappers';
import { supabase } from '../supabaseClient';
import apiClient from '../config/config';
import Nav from './Nav';
import SEO from './SEO';
import SignInModal from './SignInModal';
import { useCurrency } from '../context/CurrencyContext';
import '../assets/css/psearch.css';

function Discover() {
  const navigate = useNavigate();
  const { currency, getCurrencySymbol } = useCurrency();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  const [imageRetries, setImageRetries] = useState({});
  const MAX_RETRIES = 2;
  const [user, setUser] = useState(null);
  const [savedProducts, setSavedProducts] = useState([]);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInModalMessage, setSignInModalMessage] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState('');

  const normalizeProductId = (productId) => {
    if (productId === null || productId === undefined) return '';
    return String(productId);
  };

  // Auth listener
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Fetch saved products
  useEffect(() => {
    const fetchSavedProducts = async () => {
      if (!user) {
        setSavedProducts([]);
        return;
      }
      const { data, error } = await supabase
        .from('saved_products')
        .select('product_id')
        .eq('user_id', user.id);
      if (!error && data) {
        setSavedProducts(data.map((item) => normalizeProductId(item.product_id)));
      }
    };
    fetchSavedProducts();
  }, [user]);

  // Fetch all products from database
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get('/products/search/', { params: { currency } });
        if (response.data) {
          // Backend already handles new-brand boost + equal brand visibility
          setProducts(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleImageLoad = (productId) => {
    setLoadedImages(prev => ({ ...prev, [productId]: true }));
  };

  const handleImageError = (e, productId, originalSrc) => {
    const currentRetries = imageRetries[productId] || 0;
    const imgElement = e.currentTarget;
    
    if (currentRetries < MAX_RETRIES) {
      // Retry loading the original image after a delay
      setImageRetries(prev => ({ ...prev, [productId]: currentRetries + 1 }));
      setTimeout(() => {
        // Check if element still exists before setting src
        if (imgElement && imgElement.parentNode) {
          imgElement.src = originalSrc + '?retry=' + (currentRetries + 1);
        }
      }, 500 * (currentRetries + 1));
    } else {
      // Max retries reached, use fallback
      if (imgElement) {
        imgElement.onerror = null;
        imgElement.src = '/default-image.avif';
      }
      handleImageLoad(productId);
    }
  };

  const toggleSaveProduct = async (productId) => {
    if (!user) {
      setSignInModalMessage('Sign in to save your favorite products!');
      setShowSignInModal(true);
      return;
    }
    const normalizedProductId = normalizeProductId(productId);
    const isSaved = savedProducts.includes(normalizedProductId);

    if (isSaved) {
      const { error } = await supabase
        .from('saved_products')
        .delete()
        .eq('user_id', user.id)
        .eq('product_id', normalizedProductId);
      if (!error) {
        setSavedProducts(savedProducts.filter((id) => id !== normalizedProductId));
      }
    } else {
      const { error } = await supabase
        .from('saved_products')
        .insert([{ user_id: user.id, product_id: normalizedProductId }]);
      if (!error) {
        setSavedProducts([...savedProducts, normalizedProductId]);
      }
    }
  };

  const handleGoogleSignIn = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) console.error('Error signing in with Google:', error.message);
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    if (!authEmail) return;
    setAuthLoading(true);
    setAuthMessage('');
    const { error } = await supabase.auth.signInWithOtp({
      email: authEmail,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email for the magic link!');
    }
    setAuthLoading(false);
  };

  const handleEmailPasswordSignIn = async (email, password) => {
    setAuthLoading(true);
    setAuthMessage('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setShowSignInModal(false);
    }
    setAuthLoading(false);
  };

  const handleEmailPasswordSignUp = async (email, password) => {
    setAuthLoading(true);
    setAuthMessage('');
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: { user_role: 'general' },
      },
    });
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email to confirm your account!');
    }
    setAuthLoading(false);
  };

  return (
    <Container className="py-5">
      <SEO
        title="Discover Kashmiri Fashion"
        description="Explore curated Kashmiri fashion collections. Discover trending pashmina shawls, phiran, aari embroidery, and handcrafted designs from Kashmir's finest artisans."
        path="/discover"
      />
      <Nav />
      
      <div style={{ padding: '0', maxWidth: '1400px', margin: '0 auto' }}>
        <div className="discover-hero page-hero-fullwidth" style={{ 
          background: 'linear-gradient(135deg, rgba(245,245,245,0.8) 0%, rgba(255,255,255,0.6) 100%)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          paddingTop: '40px',
          paddingBottom: '20px',
          marginBottom: '40px',
          textAlign: 'left',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div className="fluid-blob fluid-blob-1" style={{ opacity: 0.03 }} />
          <FadeInUp delay={0.1}>
          <h1 className="discover-title" style={{ fontSize: '3rem', fontWeight: 600, fontFamily: "'Montserrat', sans-serif", color: '#1a1a1a', marginBottom: '8px', letterSpacing: '0em', position: 'relative', zIndex: 1 }}>
            D I S C O V E R
          </h1>
          </FadeInUp>
          <FadeInUp delay={0.25}>
          <p style={{ fontSize: '16px', color: '#666', fontWeight: '300', position: 'relative', zIndex: 1 }}>
            Explore and save your favorite Kashmiri fashion pieces
          </p>
          </FadeInUp>
        </div>

        <div
          style={{
            columnCount: 4,
            columnGap: '16px',
          }}
          className="discover-grid"
        >
          {products.map((product, index) => {
            const productId = product.id || product.link;
            const isLoaded = loadedImages[productId];
            
            return (
              <div
                key={index}
                style={{
                  breakInside: 'avoid',
                  marginBottom: '16px',
                  position: 'relative',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'opacity 0.4s ease-out, transform 0.4s ease-out',
                }}
                onClick={() => navigate(`/product/${product.id || encodeURIComponent(product.link)}`)}
              >
                <img
                  src={product.image}
                  alt={product.title || 'Product'}
                  loading="lazy"
                  style={{
                    width: '100%',
                    display: 'block',
                    borderRadius: '16px',
                  }}
                  onLoad={() => handleImageLoad(productId)}
                  onError={(e) => handleImageError(e, productId, product.image)}
                />
                
                {/* Heart button */}
                <button
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'rgba(255,255,255,0.95)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    transition: 'transform 0.2s',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSaveProduct(product.id ?? product.link);
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill={savedProducts.includes(normalizeProductId(product.id ?? product.link)) ? '#e63946' : 'none'}
                    stroke="#e63946"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </button>
                

                {/* Hover overlay with info */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                    padding: '40px 12px 12px',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                  }}
                  className="discover-overlay"
                >
                  <p className="yb-brand-name" style={{ color: '#fff', fontSize: '14px', fontWeight: '500', margin: 0 }}>
                    {product.brand || 'Unknown Brand'}
                    {/* {product.title || 'Unknown name'} */}
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px', margin: '4px 0 0' }}>
                    {getCurrencySymbol()} {product.price || 'N/A'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <p style={{ color: '#888' }}>Loading...</p>
          </div>
        )}
      </div>

      <SignInModal
        open={showSignInModal}
        onClose={() => {
          setShowSignInModal(false);
          setAuthMessage('');
          setAuthEmail('');
          setSignInModalMessage('');
        }}
        message={signInModalMessage}
        authEmail={authEmail}
        setAuthEmail={setAuthEmail}
        authLoading={authLoading}
        authMessage={authMessage}
        onEmailSignIn={handleEmailSignIn}
        onGoogleSignIn={handleGoogleSignIn}
        onEmailPasswordSignIn={handleEmailPasswordSignIn}
        onEmailPasswordSignUp={handleEmailPasswordSignUp}
      />

      <style>{`
        .discover-grid > div:hover .discover-overlay {
          opacity: 1 !important;
        }
        @media (max-width: 1200px) {
          .discover-grid {
            column-count: 3 !important;
          }
        }
        @media (max-width: 768px) {
          .discover-grid {
            column-count: 2 !important;
            column-gap: 10px !important;
          }
          .discover-title {
            font-size: 2rem !important;
          }
          .discover-hero {
            padding: 30px 16px !important;
          }
        }
        @media (max-width: 480px) {
          .discover-grid {
            column-count: 2 !important;
            column-gap: 8px !important;
          }
          .discover-title {
            font-size: 1.5rem !important;
          }
        }
      `}</style>
    </Container>
  );
}

export default Discover;
